import type { RefObject } from 'react';

type FocusTarget = {
  focus(): void;
};

type ReadinessWaiter = (listener: () => void) => () => void;

const readyImmediately: ReadinessWaiter = (listener) => {
  listener();
  return () => {};
};

export type TransitionEndEvent = {
  data: {
    closing: boolean;
  };
};

type StackTransitionNavigation = {
  addListener(
    event: 'transitionEnd',
    listener: (event: TransitionEndEvent) => void,
  ): () => void;
  getState(): {
    routes: readonly unknown[];
    type: string;
  };
  isFocused(): boolean;
};

type ParentNavigation = {
  getParent(): unknown;
};

function isStackTransitionNavigation(
  navigation: unknown,
): navigation is StackTransitionNavigation {
  if (typeof navigation !== 'object' || navigation === null) {
    return false;
  }

  return (
    'addListener' in navigation &&
    typeof navigation.addListener === 'function' &&
    'getState' in navigation &&
    typeof navigation.getState === 'function' &&
    navigation.getState().type === 'stack' &&
    'isFocused' in navigation &&
    typeof navigation.isFocused === 'function'
  );
}

function hasParentNavigation(
  navigation: unknown,
): navigation is ParentNavigation {
  return (
    typeof navigation === 'object' &&
    navigation !== null &&
    'getParent' in navigation &&
    typeof navigation.getParent === 'function'
  );
}

function findOpeningStackTransition(navigation: unknown) {
  let currentNavigation: unknown = navigation;

  while (currentNavigation) {
    if (
      isStackTransitionNavigation(currentNavigation) &&
      currentNavigation.getState().routes.length > 1
    ) {
      return currentNavigation;
    }

    currentNavigation = hasParentNavigation(currentNavigation)
      ? currentNavigation.getParent()
      : undefined;
  }

  return null;
}

export function scheduleAutoFocusAfterTransition<Target extends FocusTarget>(
  ref: RefObject<Target | null>,
  navigation: unknown,
  waitForKeyboardIdle: ReadinessWaiter = readyImmediately,
) {
  let nativeLayoutFrame: number | null = null;
  let focusFrame: number | null = null;
  let hasRequestedFocus = false;
  let stopWaitingForKeyboardIdle: (() => void) | null = null;

  const openingStackTransition = findOpeningStackTransition(navigation);

  const focus = () => {
    if (
      hasRequestedFocus ||
      (isStackTransitionNavigation(navigation) && !navigation.isFocused())
    ) {
      return;
    }

    hasRequestedFocus = true;
    stopWaitingForKeyboardIdle = waitForKeyboardIdle(() => {
      if (!isStackTransitionNavigation(navigation) || navigation.isFocused()) {
        nativeLayoutFrame = requestAnimationFrame(() => {
          focusFrame = requestAnimationFrame(() => ref.current?.focus());
        });
      }
    });
  };

  if (!openingStackTransition) {
    focus();
    return () => {
      if (nativeLayoutFrame !== null) {
        cancelAnimationFrame(nativeLayoutFrame);
      }
      if (focusFrame !== null) {
        cancelAnimationFrame(focusFrame);
      }
      stopWaitingForKeyboardIdle?.();
    };
  }

  const unsubscribe = openingStackTransition.addListener(
    'transitionEnd',
    (event) => {
      if (!event.data.closing) {
        focus();
      }
    },
  );

  return () => {
    unsubscribe();
    if (nativeLayoutFrame !== null) {
      cancelAnimationFrame(nativeLayoutFrame);
    }
    if (focusFrame !== null) {
      cancelAnimationFrame(focusFrame);
    }
    stopWaitingForKeyboardIdle?.();
  };
}
